import { AppId } from '@moodys/cre-cpm.models.micro-frontends';
import { AirbrakeEnvironment, NotificationSetup } from '@moodys/cre-cpm.models.logging';
import { getAppEnvironment, isProductionEnv } from '@moodys/cre-cpm.functions.environment';
import { Notifier, INotice } from '@airbrake/browser';
import { getMicrofrontends } from '@moodys/cre-cpm.functions.micro-frontends';
import {
  tryLocateErrorByFileOrigin,
  tryLocateErrorByStack,
  tryLocateErrorByRoute,
} from '@moodys/cre-cpm.functions.error-handling';

export const getAirbrakeEnvironment = (): AirbrakeEnvironment => (process.env.REACT_APP_AIRBRAKE_ENVIRONMENT ?? 'not-set') as AirbrakeEnvironment;

export const getAirbrakeProjectKeyByRemote = (
  appId: AppId,
): string => process.env[`REACT_APP_AIRBRAKE_${appId.toLocaleUpperCase()}_PROJECTKEY`] ?? '';

export const getAirbrakeProjectIdByRemote = (
  appId: AppId,
): number => parseInt(process.env[`REACT_APP_AIRBRAKE_${appId.toLocaleUpperCase()}_PROJECTID`] ?? '', 10);

export const isAirbrakeEnabled = (): boolean => getAppEnvironment() === 'production' || getAppEnvironment() === 'staging';

export const getInitializationData = (remoteId: AppId): NotificationSetup => ({
  projectId: getAirbrakeProjectIdByRemote(remoteId),
  projectKey: getAirbrakeProjectKeyByRemote(remoteId),
  environment: getAirbrakeEnvironment(),
  keysBlocklist: [/password/i, /pwd/i, /email/i, /userId/i, /secret/i, /authorization/i, /token/i],
  instrumentation: {
    console: !isProductionEnv(),
  },
});

export const hasConfiguration = (
  setup: NotificationSetup,
): boolean => setup && !!setup.projectId && !!setup.projectKey && !Number.isNaN(setup.projectId);

export const microfrontendFilter = (remoteId: AppId, customFilters?: string[]) => (notice: INotice): INotice | null => {
  const err = notice.errors?.length ? notice.errors[0] : notice.error;
  if (customFilters && customFilters.some((errorFiltered) => err?.message.startsWith(errorFiltered))) {
    return null
  }
  if (tryLocateErrorByFileOrigin(
    err,
    (err as any)?.fileName,
  ) === remoteId) return notice;
  if (tryLocateErrorByStack(err) === remoteId) return notice;
  if (tryLocateErrorByRoute() === remoteId) return notice;

  return null;
};

export const createAirbrakeNotifiers = (customFilters?: string[]): Map<AppId, Notifier> => {
  const notifiers = new Map<AppId, Notifier>();
  try {
    getMicrofrontends().forEach((remoteId) => {
      const config = getInitializationData(remoteId);
      if (hasConfiguration(config)) {
        const notifier = new Notifier(config);
        notifier.addFilter((notice) => microfrontendFilter(remoteId, customFilters)(notice));
        notifiers.set(remoteId, notifier);
      }
    });

    const configHub = getInitializationData('hubApp');
    if (hasConfiguration(configHub)) {
      const notifier = new Notifier(configHub);
      notifier.addFilter((notice) => microfrontendFilter('hubApp', customFilters)(notice));
      notifiers.set('hubApp', notifier);
    }
  } catch {
    // Do nothing here just safe function
  }

  return notifiers;
};
